import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { ErrorState } from '@/components/ui/ErrorState';
import { Skeleton } from '@/components/ui/Skeleton';
import { CalendarIcon } from '@/assets/icons/CalendarIcon';
import { useRegisteredEvents } from '@/hooks/useEvents';
import { toEventCardProps } from '@/features/events/event.mappers';
import { FROM_DASHBOARD } from '@/features/events/event-detail-back';
import { ROUTES, eventDetailPath } from '@/lib/constants/routes';
import { DASHBOARD_PREVIEW, DASHBOARD_STRINGS } from '@/lib/constants/dashboard-strings';

const S = DASHBOARD_STRINGS.REGISTERED;

/** Earliest first; an event with no start time sorts last. */
function byStart(a: { startsAt: string | null }, b: { startsAt: string | null }): number {
  return (a.startsAt ?? '￿').localeCompare(b.startsAt ?? '￿');
}

/** The next events the member is registered for (MYN-254), side column. */
export function DashboardRegisteredEvents() {
  const { data, isLoading, isError, refetch } = useRegisteredEvents();
  const events = [...(data ?? [])].sort(byStart).slice(0, DASHBOARD_PREVIEW.REGISTERED_EVENTS);

  return (
    <Card shadow="soft" hoverEffect={false} className="w-full p-6!">
      <section aria-labelledby="dashboard-registered-heading" className="flex flex-col gap-4">
        <h2 id="dashboard-registered-heading" className="text-h5 text-teal">
          {S.TITLE}
        </h2>
        {isLoading && <Skeleton rows={2} className="h-12 w-full" />}
        {isError && <ErrorState description={S.LOAD_FAILED} onRetry={() => void refetch()} />}
        {!isLoading && !isError && events.length === 0 && (
          <div className="flex flex-col items-start gap-2">
            <p className="m-0 text-sm text-slate">{S.EMPTY}</p>
            <Button asChild variant="link" className="text-sm">
              <Link to={ROUTES.EVENTS}>{S.BROWSE}</Link>
            </Button>
          </div>
        )}
        {events.length > 0 && (
          <ul className="m-0 flex list-none flex-col gap-3 p-0">
            {events.map((event) => {
              const card = toEventCardProps(event);
              return (
                <li key={event.id}>
                  <Link
                    to={eventDetailPath(event.id)}
                    state={FROM_DASHBOARD}
                    className="flex cursor-pointer items-start gap-3 rounded-lg p-2 transition-colors hover:bg-page-bg focus-visible:ring-2 focus-visible:ring-green focus-visible:outline-none"
                  >
                    <CalendarIcon size={16} className="mt-0.5 shrink-0 text-green" />
                    <span className="flex min-w-0 flex-col">
                      <span className="text-sm font-semibold text-navy">{card.title}</span>
                      <span className="text-xs text-slate">{card.dateLabel}</span>
                    </span>
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </Card>
  );
}
